import { FC, useEffect, useState } from "react";
import { SelectOptions } from "../../../../dtos/select-options";
import {
  Col,
  InputMemo,
  Modal,
  Row,
  Select,
  SelectWithIcon,
} from "../../../TrueUI";
import { rowWithNoMarginNorGutter } from "../../../TrueUI/Grids/Row";
import { selectConfiguration } from "../../../../utilities/inputPropsConfigurationFunctions";
import PolicyStatusHistoryTable from "./PolicyStatusHistoryTable";
import { useApiGet, useApiPost } from "../../../../hooks";
import { isAPITotallyComplete } from "../../../../utilities/apiFunctions";
import {
  defaultFormData,
  getClearAssignmentOptionIfNeeded,
  getCurrentAssigneeId,
  getTeamForPolicy,
  PolicyChangeStatusModalProps,
  PolicyStatusFormData,
} from "./PolicyStatusUtils";
import { PolicyStatusConfigurationDto } from "../../../../dtos/policy-status-configuration-dto";
import { usePolicyQuoteTriggerComponent } from "../hooks/usePolicyQuoteTriggerComponent";
import { INSURED_ATOM_KEY } from "../../../../utilities/queryStringsHash";
import { useAtomFamily } from "../../../../hooks/useAtomFamily";
import { GlobalInsuredAtomFamily } from "../../InsuredAtoms";
import { updateAuditStatusValueInPolicyQuote } from "../updatesPolicyQuoteFunctions";
import { AssigneeTypeEnum } from "../../../../dtos/assignee-type-enum";
import { TEAM_ICON_NAME } from "../../../../utilities/staticDataFunctions";
import { PolicyUpdateStatusDto } from "../../../../dtos/policy-update-status-dto";
import DialogConfirmation from "../../../TrueUI/Dialogs/DialogConfirmation";
import { PolicyStatusEnums } from "../../../../dtos/policy-status-enums";

const PolicyChangeStatusModal: FC<PolicyChangeStatusModalProps> = ({
  tabKey,
  modalConfiguration,
  closeModal,
  onSaveSuccessful,
  auditStatusOptions = [],
  assignmentsGroups = [],
}) => {
  // State to manage the confirmation dialog visibility
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);

  // State to manage the save button's disabled state
  const [isSaving, setIsSaving] = useState<boolean>(false);

  // Get the current assignee ID based on the policy ID
  const assigneeId = getCurrentAssigneeId(
    assignmentsGroups,
    modalConfiguration?.policyId
  );

  // State to manage the available assignee options (users and teams)
  const [assigneeOptions, setAssigneeOptions] = useState<{
    users: Partial<SelectOptions>[] | null;
    teams: Partial<SelectOptions>[] | null;
  }>({ users: null, teams: null });

  // State to manage the form data for the modal
  const [formData, setFormData] = useState<PolicyStatusFormData>({
    ...defaultFormData,
    insuredId: modalConfiguration?.insuredId,
  });

  // State to manage the available status options
  const [statusOptions, setStatusOptions] =
    useState<Partial<SelectOptions>[]>(auditStatusOptions);

  // State to manage error details for form validation
  const [errorDetails, setErrorDetails] = useState<any>();

  // Atom key for managing state across components
  const atomKey = `${INSURED_ATOM_KEY} ${tabKey}`;
  const { getAtom, setAtom, setComponentTriggers } = useAtomFamily(
    GlobalInsuredAtomFamily(atomKey)
  );
  const { setPolicyQuoteTriggers } = usePolicyQuoteTriggerComponent();

  // API hooks for fetching and posting data
  const { responseGet, dispatchGet } = useApiGet<PolicyStatusConfigurationDto>(
    "api/Policy/GetPolicyStatusConfiguration"
  );

  const { responsePost, dispatchPost, validatorErrorResponse } =
    useApiPost<PolicyUpdateStatusDto | null>(
      modalConfiguration.isAudit
        ? "api/Audit/UpdateAuditStatus"
        : "api/Policy/UpdatePolicyStatus",
      { ...formData }
    );

  // Event handler for canceling the modal
  const onCancelEvent = () => {
    setFormData(defaultFormData);
    closeModal();
  };

  // Fetch status configuration data when the modal is not in audit mode
  useEffect(() => {
    if (!modalConfiguration.isAudit) dispatchGet();
  }, []);

  // Handle the response from the POST API call
  useEffect(() => {
    if (isAPITotallyComplete(responsePost)) {
      setIsSaving(false); // Re-enable the save button
      const policyUpdated = modalConfiguration.isAudit
        ? null
        : responsePost.axiosResponse?.data;
      onSaveSuccessful?.(policyUpdated);
      setFormData(defaultFormData);
      closeModal();
      setComponentTriggers(["assignmentComponent"]);
      if (modalConfiguration.isAudit) {
        const atomValue = getAtom();
        const newAtomValue = updateAuditStatusValueInPolicyQuote(atomValue, {
          intValue: formData.status,
          displayName: formData.description ?? "",
        } as SelectOptions);
        setAtom(newAtomValue);
        setPolicyQuoteTriggers(["endorsementHeaderComponent"]);
      }
    } else if (responsePost.errorResponse) {
      setIsSaving(false); // Re-enable the save button on failure
      setErrorDetails(validatorErrorResponse?.errorDetails);
    }
  }, [responsePost]);

  // Handle the response from the GET API call
  useEffect(() => {
    if (isAPITotallyComplete(responseGet)) {
      setStatusOptions(responseGet.responseData?.statusOptions ?? []);

      setAssigneeOptions({
        users:
          responseGet.responseData?.addAssignmentSelectOptions?.users ?? [],
        teams: getTeamForPolicy(
          responseGet.responseData?.addAssignmentSelectOptions?.teams ?? []
        ),
      });
    }
  }, [responseGet]);

  // Update form data when assignee options are available
  useEffect(() => {
    if (assigneeOptions?.users !== null && assigneeOptions?.teams !== null) {
      const selectedAssignee = [
        ...(assigneeOptions?.users ?? []),
        ...(assigneeOptions?.teams ?? []),
      ]?.find((option) => option?.intValue === assigneeId);
      setFormData({
        ...formData,
        assigneeId: assigneeId,
        assigneeType:
          selectedAssignee?.iconName === TEAM_ICON_NAME
            ? AssigneeTypeEnum.TEAM
            : AssigneeTypeEnum.USER,
      });
    }
  }, [assigneeOptions]);

  // Update form data when modal configuration changes
  useEffect(() => {
    setFormData({
      ...formData,
      policyId: modalConfiguration.policyId ?? formData.policyId,
      quoteId: modalConfiguration.quoteId ?? formData.quoteId,
      status: modalConfiguration.currentStatus ?? formData.status,
      description:
        statusOptions.find(
          (s) =>
            s.intValue === (modalConfiguration.currentStatus ?? formData.status)
        )?.displayName ?? "",
    });
  }, [modalConfiguration]);

  // Event handler for selecting a new assignee
  const assigneeSelected = (assigneeId) => {
    const selectedAssignee = [
      ...(assigneeOptions?.users ?? []),
      ...(assigneeOptions?.teams ?? []),
    ]?.find((option) => option?.intValue === assigneeId);
    setFormData({
      ...formData,
      newAssigneeId: assigneeId,
      assigneeType:
        selectedAssignee?.iconName === TEAM_ICON_NAME
          ? AssigneeTypeEnum.TEAM
          : AssigneeTypeEnum.USER,
    });
  };

  // Event handler for saving the form data
  const onSaveButtonClicked = () => {
    if (formData.status === PolicyStatusEnums.DELETED) {
      setIsDialogOpen(true);
    } else {
      setIsSaving(true); // Disable the save button
      dispatchPost();
    }
  };

  return (
    <Modal
      id={"policy_change_status"}
      title={"Change Status"}
      size={"md"}
      open={modalConfiguration.isOpen}
      cancelButtonWithConfirmation
      cancelEvent={onCancelEvent}
      showCancelTextButton
      showCloseButton
      cancelButtonConfirmationText={
        "Are you sure you want to discard the changes?"
      }
      saveEvent={onSaveButtonClicked}
      saveDisabled={isSaving} // Disable the save button when saving
    >
      {/* Row for status and assignee selection */}
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 6, lg: 6, xl: 6 }}>
          <Select
            {...selectConfiguration(
              "id-policy-status",
              "policy-status",
              "New Status"
            )}
            options={statusOptions}
            labelFontType="BOLD_CAPTION"
            value={formData.status}
            errorMessage={errorDetails?.status}
            onChange={(value) =>
              setFormData({
                ...formData,
                status: value,
                description:
                  statusOptions.find((s) => s.intValue === value)
                    ?.displayName ?? "",
              })
            }
          />
        </Col>
        <Col breakpoints={{ md: 6, lg: 6, xl: 6 }}>
          <SelectWithIcon
            id="policy-assigned-user-or-team"
            label="New Assignee"
            name="insuredAssignedUserOrTeam"
            options={[
              ...getClearAssignmentOptionIfNeeded(assigneeId),
              ...(assigneeOptions?.users ?? []),
              ...(assigneeOptions?.teams ?? []),
            ]}
            value={formData?.assigneeId}
            onChange={(e) => assigneeSelected(e)}
            firstOptionAsDefault={false}
            optionsContainerPosition={"bottom"}
            labelFontType={"BOLD_CAPTION"}
            optionsMaxHeight="380px"
          />
        </Col>
      </Row>
      {/* Row for comments input */}
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 12 }}>
          <InputMemo
            id="id-policy-status-comments"
            name="policy-status-comments"
            label="Comments"
            value={formData?.comments}
            onChangeRawValue={(value) =>
              setFormData({ ...formData, comments: value })
            }
            errorMessage={errorDetails?.comments}
            rows={4}
            resize={true}
          />
        </Col>
      </Row>
      {/* Row for policy status history table */}
      <Row {...rowWithNoMarginNorGutter}>
        <Col breakpoints={{ md: 12 }}>
          <PolicyStatusHistoryTable
            policyId={modalConfiguration.policyId ?? 0}
            isAudit={modalConfiguration.isAudit}
          />
        </Col>
      </Row>
      {/* Confirmation dialog for deletion */}
      <DialogConfirmation
        open={isDialogOpen}
        dialogDescriptionText="Are you sure you want to delete this policy?"
        onOptionYesEvent={() => dispatchPost()}
        optionYesOverrideLabel="OK"
        onOptionNoEvent={() => setIsDialogOpen(false)}
      />
    </Modal>
  );
};

export default PolicyChangeStatusModal;
